/**
 * Shared normalization helpers for converting raw API payloads into typed
 * domain objects used by both the Dashboard and Intelligence pages.
 */

import { parseLocalDate } from './dateUtils'
import type { ShowData, SongData, FinanceData } from './intelligenceEngine'

export function extractArray(payload: any) {
  const list = payload?.data?.data || payload?.data || payload || []
  if (Array.isArray(list)) return list
  if (Array.isArray(list?.data)) return list.data
  return []
}

const CANCELED_STATUSES = new Set(['canceled', 'cancelled'])

export function isShowCanceled(item: { status?: string }): boolean {
  return CANCELED_STATUSES.has((item.status || '').toLowerCase().trim())
}

export function normalizeBookings(payload: any): ShowData[] {
  return extractArray(payload).filter((item: any) => !isShowCanceled(item)).map((item: any) => ({
    id: item._id || item.id,
    date: item.date || item.perf_date,
    perf_date: item.perf_date || item.date,
    title: item.event_title || item.title || item.venue,
    venue: item.venue,
    city: item.city,
    state: item.state,
    tix_sold: item.tix_sold ?? item.current_ticket_count ?? item.tickets_sold ?? item.ticket_count ?? 0,
    ticket_count: item.ticket_count ?? item.tickets_sold ?? item.tix_sold ?? item.current_ticket_count ?? 0,
    tix_sold_percent: (() => {
      const sold = item.tix_sold ?? item.current_ticket_count ?? item.tickets_sold ?? item.ticket_count ?? 0
      const cap = Number(String(item.sellable_cap || item.capacity || 0).replace(/[^0-9]/g, '')) || 0
      return cap > 0 ? Math.round((sold / cap) * 100) : undefined
    })(),
    sellable_cap: item.sellable_cap,
    capacity: item.capacity,
    guarantee: item.guarantee,
    bonus: item.bonus,
    status: item.status,
    show_time: item.show_time,
    event_title: item.event_title || item.title,
    ticket_tracking:
      item.ticket_tracking === false || item.ticket_tracking === 'false' || item.ticket_tracking === 0
        ? false
        : item.ticket_tracking === true || item.ticket_tracking === 'true' || item.ticket_tracking === 1
          ? true
          : undefined,
    raw: item,
  }))
}

export function normalizeCatalog(payload: any): SongData[] {
  return extractArray(payload).map((item: any) => ({
    id: item._id || item.id,
    song_title: item.song_title || item.title || item.songName,
    title: item.title || item.song_title,
    streams_total: Number(item.streams_total ?? item.streams ?? item.streamsTotal ?? item?.soundcharts_data?.streams ?? 0) || 0,
    streams_manual_override: item.streams_manual_override || item.streamsManualOverride,
    streams_today: Number(item.streams_today ?? item.streamsToday ?? item.streams_daily ?? 0) || undefined,
    release_date: item.release_date || item.releaseDate,
    artist_name: item.artist_name || item.artistName || item.artist,
    status: item.status,
    raw: item,
  }))
}

export function normalizeFinancials(payload: any): FinanceData[] {
  const normalizeFinanceType = (rawType: unknown): 'revenue' | 'spend' => {
    const t = String(rawType || '').toLowerCase().trim()
    if (t === 'revenue' || t === 'income' || t === 'credit' || t === 'inflow') return 'revenue'
    return 'spend'
  }

  const resolveFinanceDate = (item: any): string => {
    return (
      item.date ||
      item.transaction_date ||
      item.entry_date ||
      item.posted_date ||
      item.created_at ||
      item.createdAt ||
      item.updated_at ||
      item.updatedAt ||
      ''
    )
  }

  return extractArray(payload).map((item: any) => ({
    id: item._id || item.id,
    date: resolveFinanceDate(item),
    due_date:
      item.due_date ||
      item.dueDate ||
      item.payment_due_date ||
      item.paymentDueDate ||
      '',
    type: normalizeFinanceType(item.type),
    amount: Number(item.amount) || 0,
    description: item.description,
    source: item.source || 'other',
    income_type: item.income_type,
    ...(item.digestContextArtistId
      ? { digestContextArtistId: String(item.digestContextArtistId) }
      : {}),
  })).filter((row: FinanceData) => Boolean(row.date))
}

export function parseMoneyAmount(value: unknown): number {
  if (value == null || value === '') return 0
  const normalized = String(value).replace(/[^0-9.-]/g, '')
  const n = Number(normalized)
  return Number.isFinite(n) ? n : 0
}

export function deriveShowFinanceEntries(showsPayload: any): FinanceData[] {
  const out: FinanceData[] = []
  for (const show of extractArray(showsPayload)) {
    if (isShowCanceled(show)) continue
    const perfDate = show?.perf_date ?? show?.date ?? ''
    const date = typeof perfDate === 'string' ? perfDate.split('T')[0] : ''
    if (!date) continue
    const showId = show?._id ?? show?.id ?? ''
    const showName = show?.event_title || show?.venue || 'Show'

    const guaranteeAmt = parseMoneyAmount(show?.guarantee)
    const walkoutAmt = parseMoneyAmount(show?.walkout_potential)
    const hasWalkoutOnly = walkoutAmt > guaranteeAmt

    const showAid =
      show?.digestContextArtistId != null ? { digestContextArtistId: String(show.digestContextArtistId) } : {}
    if (guaranteeAmt > 0 && !hasWalkoutOnly) {
      out.push({
        id: `derived-show-guarantee-${showId || showName}-${date}`,
        date,
        type: 'revenue',
        amount: guaranteeAmt,
        description: `${showName} - Guarantee`,
        source: show?.venue || show?.promoter || 'shows',
        ...showAid,
      })
    }
    if (hasWalkoutOnly) {
      out.push({
        id: `derived-show-walkout-${showId || showName}-${date}`,
        date,
        type: 'revenue',
        amount: walkoutAmt,
        description: `${showName} - Walkout potential`,
        source: show?.venue || show?.promoter || 'shows',
        ...showAid,
      })
    }
  }
  return out
}

export function deriveDealFinanceEntries(dealsPayload: any): FinanceData[] {
  const raw = dealsPayload?.data ?? dealsPayload
  const list = Array.isArray(raw?.deals) ? raw.deals : Array.isArray(raw) ? raw : []
  const out: FinanceData[] = []
  for (const deal of list) {
    const amt = Number(deal?.expected_value ?? deal?.expectedValue) || 0
    if (amt <= 0) continue
    const stage = String(deal?.stage || '').toLowerCase()
    if (stage === 'lost') continue
    const rawDate =
      deal?.payment_posted_date ??
      deal?.paymentPostedDate ??
      deal?.target_start ??
      deal?.targetStart ??
      deal?.created_at ??
      deal?.createdAt
    const date = typeof rawDate === 'string'
      ? rawDate.split('T')[0]
      : rawDate
        ? new Date(rawDate).toISOString().split('T')[0]
        : ''
    if (!date) continue
    const dealId = deal?._id ?? deal?.id ?? ''
    const brand = deal?.brand_name ?? deal?.brandName ?? ''
    const dealAid =
      deal?.digestContextArtistId != null ? { digestContextArtistId: String(deal.digestContextArtistId) } : {}
    out.push({
      id: `derived-deal-${dealId || brand || date}-${date}`,
      date,
      type: 'revenue',
      amount: amt,
      description: `${deal?.name || 'Deal'}${brand ? ` - ${brand}` : ''}`,
      source: brand || 'deals',
      ...dealAid,
    })
  }
  return out
}

export function mergeUniqueFinances(base: FinanceData[], derived: FinanceData[]): FinanceData[] {
  const bySemanticKey = new Set<string>()
  const merged: FinanceData[] = []
  const pushUnique = (row: FinanceData) => {
    const key = [
      row.type,
      row.date || '',
      Number(row.amount || 0).toFixed(2),
      (row.description || '').trim().toLowerCase(),
    ].join('|')
    if (bySemanticKey.has(key)) return
    bySemanticKey.add(key)
    merged.push(row)
  }
  base.forEach(pushUnique)
  derived.forEach(pushUnique)
  return merged
}

export function getDaysUntilRelease(releaseDate: string | undefined): number {
  if (!releaseDate) return -1
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = parseLocalDate(releaseDate)
  if (!d) return -1
  d.setHours(0, 0, 0, 0)
  return Math.ceil((d.getTime() - today.getTime()) / 86_400_000)
}

export function isUpcomingSong(s: SongData): boolean {
  const st = (s.status || '').toLowerCase()
  if (st === 'demo') return false
  if (!['unreleased', 'upcoming'].includes(st)) return false
  return getDaysUntilRelease(s.release_date) >= 0
}

export function hasMissingCredits(s: SongData): boolean {
  const raw = s.raw || {}
  const hasMaster = Array.isArray(raw.master_contributors) && raw.master_contributors.length > 0
  const hasPub = Array.isArray(raw.publishing_contributors) && raw.publishing_contributors.length > 0
  return !hasMaster || !hasPub
}
